import { Layout } from "../components/Layout";
import { BackHome, SectionHeader, StatBlock, FeatureCard, GreenCta } from "../components/Shared";
import { SeoText } from "../components/SeoText";
import { useSeo } from "../hooks/useSeo";
import { ArrowRight, MessageCircle, Zap, Settings, Shield, Phone, Check } from "lucide-react";
import { WHATSAPP_LINK } from "../lib/api";

const stats = [
  { value: "12 ans", label: "Entrepreneur à Genève" },
  { value: "35+ ans", label: "Expertise terrain" },
  { value: "6+", label: "Workflows déployés" },
  { value: "2", label: "Entreprises automatisées" },
];

const methode = [
  { num: "01", Icon: Zap, title: "Terrain d'abord", text: "Chaque solution que je propose a été testée dans mes propres entreprises avant d'arriver chez vous." },
  { num: "02", Icon: Settings, title: "Stack éprouvé", text: "n8n · Claude API · WhatsApp Business · Google Sheets · Supabase. Des outils robustes." },
  { num: "03", Icon: Shield, title: "Données en Suisse", text: "Hébergement suisse, conformité LPD/FADP. Vos données ne quittent pas la Suisse." },
  { num: "04", Icon: Phone, title: "Disponible vraiment", text: "WhatsApp direct, pas de ticket. Le soir, le week-end, quand vous en avez besoin." },
];

const Apropos = () => {
  useSeo({
    title: "Christophe Battras — Consultant IA & Automatisation à Genève",
    description: "Entrepreneur genevois, j'ai automatisé mes propres PME avant d'aider les autres. Je construis des systèmes IA opérationnels pour les entreprises de Suisse romande.",
    path: "/a-propos",
  });

  return (
    <Layout>
      <BackHome />

      <section className="px-6 pt-10 pb-12 md:pt-16 text-center">
        <div className="max-w-4xl mx-auto fade-in">
          <span className="pill-badge mb-5">👤 À propos</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold title-text leading-[1.05] tracking-tight">
            J'ai automatisé mon entreprise à Genève — je fais{" "}
            <span className="accent-text">la même chose pour vous</span>
          </h1>
          <p className="body-text text-lg mt-7 leading-relaxed max-w-2xl mx-auto">
            J'ai automatisé mes propres entreprises avant de conseiller les vôtres. On parle chiffres, pas de jargon.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap mt-8 justify-center">
            <a href="/audit-ia" className="btn-primary" data-testid="apropos-audit-cta">
              Réserver un audit gratuit <ArrowRight size={18} />
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <MessageCircle size={18} /> Discuter sur WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="px-6 pb-10">
        <div className="max-w-5xl mx-auto fade-in">
          <StatBlock stats={stats} testId="apropos-stats" />
        </div>
      </section>

      <section className="px-6 py-16 section-alt">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            badge="Mon parcours d'entrepreneur"
            title="Deux entreprises."
            accentTitle="Un laboratoire IA."
            subtitle="Avant de proposer ces solutions à des clients, je les ai testées dans mes propres sociétés. Ce que je vous vends, je l'utilise tous les jours."
          />
          <div className="grid md:grid-cols-2 gap-6">
            <ParcoursCard
              badge="Valet parking · Genève Aéroport"
              title="Entreprise 1 — Service de mobilité premium"
              text="Service de valet parking à l'aéroport de Genève. Entièrement automatisé : chatbot WhatsApp, emails automatiques, rapports quotidiens, rappels clients, planning en temps réel."
              items={[
                "Chatbot WhatsApp avec mémoire",
                "Email automatique IMAP + Gmail",
                "Rapport quotidien automatique",
                "Rappel client J-1 automatique",
                "Planning agent temps réel",
              ]}
            />
            <ParcoursCard
              badge="Chauffeur VIP · 35 ans d'expertise"
              title="Entreprise 2 — Service chauffeur VIP"
              text="Service de chauffeur haut de gamme à Genève. Clientèle internationale, organisations mondiales, banques privées et hôtels de luxe."
              items={[
                "Prospection automatisée",
                "CRM intégré",
                "Suivi pipeline commercial",
              ]}
            />
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            badge="Pourquoi j'ai commencé à automatiser"
            title="Je construis."
            accentTitle="Puis je conseille."
            subtitle="Je ne vends pas de l'IA en théorie. Je déploie des systèmes qui tournent."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {methode.map((m) => <FeatureCard key={m.num} {...m} />)}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="max-w-4xl mx-auto rounded-3xl p-10 md:p-16 text-center fade-in" style={{ background: "linear-gradient(135deg,#3ecf8e 0%,#2db97a 100%)" }}>
          <p className="text-white font-serif text-2xl md:text-3xl leading-snug italic mb-6">
            « En 2026, les PME qui n'automatisent pas travaillent pour celles qui le font. »
          </p>
          <p className="text-white/90 font-semibold">— Christophe Battras, Genève</p>
        </div>
      </section>

      <section className="px-6 py-16 section-alt">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Ce qui me différencie d'une agence digitale" title="Ils ont" accentTitle="franchi le pas." />
          <div className="grid md:grid-cols-2 gap-6">
            <Testimonial quote="Mon assistant WhatsApp répond à mes clients à 23h. J'ai récupéré 6h par semaine." author="Marc T." role="Dirigeant, société de transport VIP" />
            <Testimonial quote="Le rapport quotidien automatique m'arrive chaque matin. Je n'ouvre plus Excel." author="Sophie R." role="Gérante, parking privé genevois" />
          </div>
        </div>
      </section>

      <GreenCta
        badge="Travaillons ensemble"
        title="Prêt à automatiser votre entreprise ?"
        subtitle="Commençons par un audit gratuit de 60 min. Sans engagement. Limité à 4 par mois."
        primaryLabel={<>Réserver mon audit <ArrowRight size={18} /></>}
        primaryAction={() => (window.location.href = "/audit-ia")}
        secondaryLabel="WhatsApp"
        secondaryAction={WHATSAPP_LINK}
        footnotes={["Gratuit", "Basé à Genève", "Réponse sous 24h"]}
      />

      <SeoText>
        <h2 className="section-title mb-6">
          Entrepreneur genevois, <span className="accent-text">pas une agence</span>
        </h2>
        <p className="body-text leading-relaxed mb-5">
          Je m'appelle Christophe Battras et je suis entrepreneur à Genève depuis plus
          de 12 ans. J'ai dirigé deux entreprises de services premium dans l'arc
          lémanique : un service de valet parking à l'aéroport de Genève et une
          activité de chauffeur VIP haut de gamme, avec une clientèle internationale
          composée d'organisations mondiales, banques privées et hôtels de luxe — soit
          plus de 35 années cumulées d'expérience terrain dans la prestation de service
          aux exigences élevées.
        </p>
        <p className="body-text leading-relaxed mb-5">
          J'ai commencé à automatiser parce que je n'avais pas le choix. Diriger deux
          PME en simultané avec des marges serrées m'a forcé à inventer des systèmes
          qui font à ma place tout ce qui peut être fait sans moi : répondre à un
          prospect à 23h, envoyer un rappel J-1, générer un rapport quotidien, qualifier
          un nouveau lead, mettre à jour un CRM, suivre un pipeline commercial. C'est
          devenu un laboratoire grandeur nature, où chaque idée a été testée d'abord
          chez moi avant d'être proposée à d'autres.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Ce qui me différencie d'une agence digitale classique tient en trois points.
          Premier point : je ne sous-traite rien. Quand vous me parlez, c'est moi qui
          construis votre système — pas un junior, pas un freelance offshore.
          Deuxième point : je suis vous. Je dirige des entreprises, je sais ce que
          veut dire un client mécontent un dimanche soir, je sais ce que veut dire
          une marge sous pression, je sais ce que veut dire "urgent" sans jargon.
          Troisième point : je suis joignable. WhatsApp direct, soir et week-end inclus.
          Pas de ticket, pas d'attente, pas de SAV à plusieurs niveaux.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Mon stack technique est volontairement éprouvé : n8n pour l'orchestration,
          Claude et GPT pour l'intelligence linguistique, WhatsApp Business pour la
          messagerie, Google Workspace et Microsoft 365 pour les intégrations
          bureautiques, Supabase pour les bases de données. Aucune techno hype, que
          des outils robustes que je connais à fond et qui tournent en production
          tous les jours dans mes propres entreprises.
        </p>
        <p className="body-text leading-relaxed">
          Si vous dirigez une PME à Genève, Lausanne ou ailleurs en Suisse romande et
          que vous voulez comprendre concrètement ce que l'IA peut changer dans votre
          activité — pas en théorie, mais avec des chiffres — réservez un audit gratuit.
          On parle de votre business, et vous repartez avec un plan d'action. C'est tout.
        </p>
      </SeoText>
    </Layout>
  );
};

const ParcoursCard = ({ badge, title, text, items }) => (
  <div className="card-base border-l-4 fade-in" style={{ borderLeftColor: "#3ecf8e" }}>
    <span className="pill-badge mb-4">{badge}</span>
    <h3 className="title-text text-xl font-bold mb-3">{title}</h3>
    <p className="body-text text-sm leading-relaxed mb-5">{text}</p>
    <ul className="space-y-2">
      {items.map((i, k) => (
        <li key={k} className="flex items-start gap-2 text-sm title-text">
          <Check size={16} className="accent-text mt-1 shrink-0" />
          {i}
        </li>
      ))}
    </ul>
  </div>
);

const Testimonial = ({ quote, author, role }) => (
  <div className="card-base fade-in">
    <p className="accent-text text-4xl font-serif leading-none mb-3">"</p>
    <p className="title-text font-medium leading-relaxed mb-4">{quote}</p>
    <p className="text-sm body-text">
      <span className="title-text font-bold">{author}</span> — {role}
    </p>
  </div>
);

export default Apropos;
